import { Fragment, useState } from "react";
import { Button, Input } from "reactstrap";
import { Upload } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { errorToast, successToast } from "../../shared/Toast";
import placeholder from "../../assets/images/placeholder.png";
import ToggleSwitch from "../../shared/ToggleSwitch";
import axios from "../../utils/axios";
import { useNavigate } from "react-router-dom";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

const INITIAL = {
  name: "",
  reference: "",
  url: "",
  email: "",
  password: "",
  db: "",
  source: "",
  menuColor: "#ffffff",
  enableMenuOrdering: false,
  enableMenuPagination: true,
  disableBackToMenus: false,
  forceOnlineOrdersAsDelivery: false,
};

// Declared outside Add_Merchant on purpose: a component defined inside another is a new type on
// every render, so React remounts it on each keystroke and the input loses focus.
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 13,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  background: C.white,
  color: C.text,
  outline: "none",
  fontFamily: "inherit",
  transition: "border 0.15s",
};

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: C.textMed,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 6,
};

const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>
      {label}
      {required && <span style={{ color: C.coral, marginLeft: 4 }}>*</span>}
    </label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 14,
      fontWeight: 700,
      color: C.text,
      marginBottom: 16,
      paddingBottom: 10,
      borderBottom: `2px solid ${C.borderLight}`,
    }}
  >
    {children}
  </div>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 14px",
      borderRadius: 10,
      border: `1px solid ${C.borderLight}`,
      background: C.bg,
      marginBottom: 10,
    }}
  >
    <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</span>
    <ToggleSwitch checked={checked} handleToggle={onChange} />
  </div>
);

const RadioOption = ({ value, label, checked, onChange }) => (
  <label
    style={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 14px",
      borderRadius: 10,
      border: `1.5px solid ${checked ? C.coral : C.border}`,
      background: checked ? C.coralLight : C.white,
      cursor: "pointer",
      transition: "all 0.15s",
      fontWeight: 600,
      color: checked ? C.coral : C.text,
      fontSize: 13,
    }}
  >
    <input
      type="radio"
      name="source"
      value={value}
      checked={checked}
      onChange={onChange}
      style={{ accentColor: C.coral, width: 16, height: 16 }}
    />
    {label}
  </label>
);

const Add_Merchant = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [merchantData, setMerchantData] = useState(INITIAL);
  const [previewImage, setPreviewImage] = useState(placeholder);
  const [imageFile, setImageFile] = useState(null);

  const handleInputChange = (key, value) => {
    setMerchantData((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const resetStates = () => {
    setMerchantData(INITIAL);
    setPreviewImage(placeholder);
    setImageFile(null);
  };

  const handleDiscard = () => resetStates();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["name", "reference", "url", "email", "password", "db", "source"];
    for (const field of required) {
      if (!merchantData[field]) {
        errorToast(field === "source" ? "Please select merchant type." : `Please fill in the ${field} field.`);
        return;
      }
    }
    if (!imageFile) {
      errorToast("Image is required.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      Object.entries(merchantData).forEach(([k, v]) => formData.append(k, v));
      formData.append("dataType", "merchant");
      formData.append("image1", imageFile);

      const response = await axios.post("api/merchant/addNewMerchant", formData, { rawRequest: true });
      if (response.data.isError === false) {
        resetStates();
        successToast("Merchant added successfully");
        navigate("/merchant/merchant-list");
      } else {
        errorToast(response.data.message || "Failed to add Merchant");
      }
    } catch (error) {
      console.error("Error adding Merchant:", error.response?.data || error);
      errorToast(error.response?.data?.message || "An error occurred while adding the merchant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Add Merchant" parent="Merchant" />
      <div className="container-fluid">
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {loading ? (
            <RLoader />
          ) : (
            <form onSubmit={handleSubmit}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "320px 1fr",
                  gap: 20,
                  alignItems: "start",
                }}
              >
                {/* Image card */}
                <RCard>
                  <SectionTitle>Merchant Image</SectionTitle>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "1",
                        borderRadius: 14,
                        border: `2px dashed ${C.border}`,
                        background: C.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <img
                        src={previewImage}
                        alt="preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: previewImage === placeholder ? "contain" : "cover",
                          padding: previewImage === placeholder ? 40 : 0,
                        }}
                      />
                    </div>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 18px",
                        borderRadius: 10,
                        background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                        color: C.white,
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                      }}
                    >
                      <Upload size={14} />
                      Upload Image
                      <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
                    </label>
                    <div style={{ fontSize: 11, color: C.textLight, textAlign: "center" }}>
                      Square image recommended (JPG/PNG)
                    </div>
                  </div>
                </RCard>

                {/* Form card */}
                <RCard>
                  <SectionTitle>Merchant Details</SectionTitle>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Field label="Name" required>
                      <input style={inputStyle} value={merchantData.name} onChange={(e) => handleInputChange("name", e.target.value)} />
                    </Field>
                    <Field label="Reference" required>
                      <input style={inputStyle} value={merchantData.reference} onChange={(e) => handleInputChange("reference", e.target.value)} />
                    </Field>
                    <Field label="URL" required>
                      <input style={inputStyle} value={merchantData.url} onChange={(e) => handleInputChange("url", e.target.value)} />
                    </Field>
                    <Field label="Database" required>
                      <input style={inputStyle} value={merchantData.db} onChange={(e) => handleInputChange("db", e.target.value)} />
                    </Field>
                    <Field label="Odoo Email" required>
                      <input type="email" style={inputStyle} value={merchantData.email} onChange={(e) => handleInputChange("email", e.target.value)} />
                    </Field>
                    <Field label="Odoo Password" required>
                      <input
                        type="password"
                        autoComplete="new-password"
                        style={inputStyle}
                        value={merchantData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                      />
                    </Field>
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <Field label="Menu Color">
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div
                          onClick={() => document.getElementById("menuColorPicker").click()}
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            background: merchantData.menuColor,
                            border: `2px solid ${C.border}`,
                            cursor: "pointer",
                            boxShadow: C.shadow,
                          }}
                        />
                        <Input
                          id="menuColorPicker"
                          type="color"
                          value={merchantData.menuColor}
                          onChange={(e) => handleInputChange("menuColor", e.target.value)}
                          style={{ display: "none" }}
                        />
                        <div
                          style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            background: C.bg,
                            border: `1px solid ${C.borderLight}`,
                            fontWeight: 600,
                            color: C.textMed,
                            fontSize: 12,
                            letterSpacing: 0.5,
                          }}
                        >
                          {merchantData.menuColor}
                        </div>
                      </div>
                    </Field>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <label style={{ ...labelStyle, marginBottom: 10 }}>
                      Merchant Type <span style={{ color: C.coral, marginLeft: 4 }}>*</span>
                    </label>
                    <div style={{ display: "flex", gap: 12 }}>
                      <RadioOption
                        value="internal"
                        label="Internal"
                        checked={merchantData.source === "internal"}
                        onChange={(e) => handleInputChange("source", e.target.value)}
                      />
                      <RadioOption
                        value="external"
                        label="External"
                        checked={merchantData.source === "external"}
                        onChange={(e) => handleInputChange("source", e.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: 24 }}>
                    <SectionTitle>Menu Preferences</SectionTitle>
                    <ToggleRow
                      label="Enable Menu Ordering"
                      checked={merchantData.enableMenuOrdering}
                      onChange={(v) => handleInputChange("enableMenuOrdering", v)}
                    />
                    <ToggleRow
                      label="Enable Menu Pagination"
                      checked={merchantData.enableMenuPagination}
                      onChange={(v) => handleInputChange("enableMenuPagination", v)}
                    />
                    <ToggleRow
                      label="Disable Back to Menus"
                      checked={merchantData.disableBackToMenus}
                      onChange={(v) => handleInputChange("disableBackToMenus", v)}
                    />
                    <ToggleRow
                      label="Force Online Orders as Delivery"
                      checked={merchantData.forceOnlineOrdersAsDelivery}
                      onChange={(v) => handleInputChange("forceOnlineOrdersAsDelivery", v)}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 10,
                      marginTop: 24,
                      paddingTop: 20,
                      borderTop: `1px solid ${C.borderLight}`,
                    }}
                  >
                    <Button
                      type="button"
                      onClick={handleDiscard}
                      style={{
                        padding: "10px 22px",
                        borderRadius: 10,
                        background: C.white,
                        color: C.text,
                        border: `1.5px solid ${C.border}`,
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      style={{
                        padding: "10px 24px",
                        borderRadius: 10,
                        background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
                        border: "none",
                        color: C.white,
                        fontWeight: 700,
                        fontSize: 13,
                        boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                      }}
                    >
                      Add Merchant
                    </Button>
                  </div>
                </RCard>
              </div>
            </form>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default Add_Merchant;
